<template>
    <div :ref="reference" class="bg-video">
        <video ref="videoEl" :preload="preload" :muted="muted?true:false" :autoplay="autoplay?true:false" :playsinline="autoplay?true:false" :loop="loop?true:false" class="video" :class="videoClass" :style="styles">
            <source :src="src" type="video/mp4">
        </video>

        <transition name="fade">
            <div v-if="poster && !playing" class="poster" :style="{'backgroundImage': 'url('+poster+')'}">
                <img :src="poster" @load="posterLoaded" style="display:none;" alt="">
            </div>
        </transition>

        <div v-if="overlay" class="overlay-video" :style="{'backgroundColor': overlay}"></div>

        <div v-if="pattern" class="overlay-pattern" :style="{'backgroundImage': `url(${pattern})`}"></div>

        <button v-if="!playing" type="button" class="btn-play" @click="playVideo()">
            <slot name="btnplay"></slot>
        </button>

        <button v-if="btns.fullscreen" type="button" class="video-full-btn" @click="playFullscreen($event)">
            <i class="icon-expand icon-l"></i>
        </button>

        <slot></slot>
    </div>
</template>

<script>
    export default {
        props: {
            src: {
                type: String,
                required: true,
                default: null
            },
            videoClass: {
                type: String,
                required: false,
                default: null
            },
            preload: {
                type: String,
                required: false,
                default: 'none'
            },
            poster: {
                type: String,
                required: false,
                default: null
            },
            muted: {
                type: Boolean,
                required: false,
                default: true
            },
            autoplay: {
                type: Boolean,
                required: false,
                default: true
            },
            loop: {
                type: Boolean,
                required: false,
                default: true
            },
            overlay: {
                type: String,
                required: false,
                default: null
            },
            pattern: {
                type: String,
                required: false,
                default: null
            },
            btns: {
                type: Object,
                required: false,
                default: function () {
                    return {
                        fullscreen: false
                    }
                }
            },
            reference: {
                type: String,
                required: false,
                default: 'video-wrapper'
            },
            initFill: {
                type: String,
                required: false,
                default: null
            },
        },
        data() {
            return {
                playing: false,
                fill: null,
                videoWrapperSize: {},
                videoElSize: {}
            }
        },
        computed: {
            styles() {
                let styles = {}

                if (this.fill === 'width') {
                    styles.width = '100%'
                } else if (this.fill === 'height') {
                    styles.height = '100%'
                }

                return styles
            },
            videoWrapperRatio() {
                return this.videoWrapperSize.width / this.videoWrapperSize.height
            }
        },
        watch: {
            playing: function (val) {
                if (val) {
                    this.$refs.videoEl.play();
                } else {
                    this.$refs.videoEl.pause();
                }
            },
            videoWrapperSize: function () {
                this.calcFill()
            }
        },
        methods: {
            playFullscreen(e) {
                var $video = this.$refs.videoEl;
                if ($video.requestFullScreen) {
                    $video.requestFullScreen();
                } else if ($video.webkitRequestFullScreen) {
                    $video.webkitRequestFullScreen();
                } else if ($video.mozRequestFullScreen) {
                    $video.mozRequestFullScreen();
                }
                $video.play();
            },
            playVideo() {
                this.playing = true;
            },
            pauseVideo() {
                this.$refs.videoEl.pause();
            },
            loadVideo() {
                this.$refs.videoEl.load();
            },
            posterLoaded() {
                this.$emit('poster-loaded');
            },
            calcVideoWrapperSize() {
                this.videoWrapperSize = {
                    width: this.$refs['video-wrapper'].offsetWidth,
                    height: this.$refs['video-wrapper'].offsetHeight,
                }
            },
            calcVideoElSize() {
                this.videoElSize = {
                    width: this.$refs['videoEl'].videoWidth,
                    height: this.$refs['videoEl'].videoHeight,
                    ratio: this.$refs['videoEl'].videoWidth / this.$refs['videoEl'].videoHeight
                }
            },
            calcFill() {
                if (!this.fill && this.initFill) {
                    this.fill = this.initFill
                }

                if (this.videoWrapperRatio > this.videoElSize.ratio) {
                    this.fill = 'width'
                } else if (this.videoWrapperRatio) {
                    this.fill = 'height'
                }
            }
        },
        created() {
            window.addEventListener('resize', () => {
                setTimeout(() => {
                    this.calcVideoWrapperSize()
                }, 100)
            }, {
                passive: true
            })
        },
        mounted() {
            this.$refs.videoEl.oncanplaythrough = () => {
                this.$emit('video-can-play');
                if (this.autoplay) {
                    this.playing = true;
                }
            }

            this.$refs.videoEl.onpause = () => {
                this.playing = false;
            }

            this.$refs.videoEl.onended = () => {
                this.$emit('video-ended');
            }

            this.$refs.videoEl.ontimeupdate = () => {
                this.$emit('video-time-update', this.$refs.videoEl);
            }

            this.$refs.videoEl.onloadedmetadata = () => {
                this.calcVideoElSize()
                this.calcVideoWrapperSize()
            }

        },
        destroyed() {
            window.removeEventListener('resize')
        }
    }
</script>

<style lang="scss">
    bg-video {
        display: block;
        position: relative;
    }

    .bg-video {
        position: relative;
        overflow: hidden;
        width: 100%;
        height: 100%;

        .video {
            min-width: 100%;
            min-height: 100%;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }

        .poster {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-size: cover;
            background-position: 50%;
        }

        .btn-play {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }

        .video-full-btn {
            position: absolute;
            bottom: 25%;
            right: 10%;
            color: #fff;
            opacity: .5;

            &:hover {
                opacity: 1;
            }
        }

        .overlay-video {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
        }

        .overlay-pattern {
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            height: 100%;
            opacity: 0.6;
        }

        .fade-enter-active,
        .fade-leave-active {
            transition: opacity 1s;
        }

        .fade-enter,
        .fade-leave-to {
            opacity: 0;
        }
    }

    @media only screen and (max-width: 991px) {
        .bg-video {
            .video-full-btn {
                bottom: 5%;
            }
        }
    }
</style>